import { create } from "zustand";
import type { TrackPoint, AssetTrack } from "../api/rest";
import { fetchTracks } from "../api/rest";
import { useTelemetryStore } from "./telemetryStore";

// Exported so it can be unit-tested independently.
export function interpolateTrack(
  points: TrackPoint[],
  tsMs: number
): { x: number; y: number; yaw: number } | null {
  if (points.length === 0) return null;
  if (tsMs <= points[0].ts) return { x: points[0].x, y: points[0].y, yaw: points[0].yaw };
  const last = points[points.length - 1];
  if (tsMs >= last.ts) return { x: last.x, y: last.y, yaw: last.yaw };

  // Binary search for the bracketing pair.
  let lo = 0, hi = points.length - 1;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (points[mid].ts <= tsMs) lo = mid; else hi = mid;
  }
  const a = points[lo], b = points[hi];
  const t = (tsMs - a.ts) / (b.ts - a.ts);
  return {
    x:   a.x   + (b.x   - a.x)   * t,
    y:   a.y   + (b.y   - a.y)   * t,
    yaw: a.yaw + (b.yaw - a.yaw) * t,
  };
}

export interface AccumPoint { assetId: string; x: number; y: number; ts: number; }

interface HistoryState {
  mode: "live" | "history";
  rangeFrom:   number;
  rangeTo:     number;
  playheadMs:  number;
  playing:     boolean;
  speed:       1 | 2 | 5 | 10;
  tracks:      Record<string, TrackPoint[]>;
  loading:     boolean;
  error:       string | null;
  positionAccumulator: AccumPoint[];

  setMode:       (m: "live" | "history") => void;
  setRange:      (from: number, to: number) => void;
  setPlayhead:   (ms: number) => void;
  togglePlay:    () => void;
  setSpeed:      (s: 1 | 2 | 5 | 10) => void;
  loadTracks:    (siteId: string, from: number, to: number) => Promise<void>;
  accumulateLive:(pts: Array<{ id: string; x: number; y: number; lastSeenMs: number }>) => void;
}

// Module-level timer so it survives store resets in tests.
let _playInterval: ReturnType<typeof setInterval> | undefined;

export const useHistoryStore = create<HistoryState>((set, get) => ({
  mode:               "live",
  rangeFrom:          0,
  rangeTo:            0,
  playheadMs:         0,
  playing:            false,
  speed:              1,
  tracks:             {},
  loading:            false,
  error:              null,
  positionAccumulator:[],

  setMode(m) { set({ mode: m }); },
  setRange(from, to) { set({ rangeFrom: from, rangeTo: to }); },

  setPlayhead(ms) {
    set({ playheadMs: ms });
    // Push interpolated poses into telemetryStore so InspectorPanel reflects the scrubbed position.
    const { tracks } = get();
    const applyDelta = useTelemetryStore.getState().applyDelta;
    for (const [assetId, pts] of Object.entries(tracks)) {
      const pose = interpolateTrack(pts, ms);
      if (pose) applyDelta({ id: assetId, ...pose, lastSeenMs: Date.now() });
    }
  },

  togglePlay() {
    clearInterval(_playInterval); // always clear first to prevent double-interval leak
    const s = get();
    if (s.playing) {
      set({ playing: false });
      return;
    }
    set({ playing: true });
    _playInterval = setInterval(() => {
      const { playheadMs, rangeTo, speed, tracks } = get();
      const next = playheadMs + 50 * speed;
      if (next >= rangeTo) {
        clearInterval(_playInterval);
        set({ playing: false, playheadMs: rangeTo });
        return;
      }
      set({ playheadMs: next });
      // Push interpolated poses to telemetryStore.
      const applyDelta = useTelemetryStore.getState().applyDelta;
      for (const [assetId, pts] of Object.entries(tracks)) {
        const pose = interpolateTrack(pts, next);
        if (pose) applyDelta({ id: assetId, ...pose, lastSeenMs: Date.now() });
      }
    }, 50);
  },

  setSpeed(s) { set({ speed: s }); },

  async loadTracks(siteId, from, to) {
    set({ loading: true, error: null });
    try {
      const raw: AssetTrack[] = await fetchTracks(siteId, from, to);
      const tracks: Record<string, TrackPoint[]> = {};
      for (const { asset_id, points } of raw) tracks[asset_id] = points;
      set({ tracks, rangeFrom: from, rangeTo: to, playheadMs: from, loading: false });
    } catch (e: unknown) {
      set({ loading: false, error: String(e) });
    }
  },

  accumulateLive(forklifts) {
    const now = Date.now();
    const THIRTY_MIN = 30 * 60 * 1_000;
    set((s) => {
      const pruned = s.positionAccumulator.filter((p) => now - p.ts < THIRTY_MIN);
      const fresh = forklifts
        .filter((f) => f.lastSeenMs && now - f.lastSeenMs < 5_000)
        .map((f): AccumPoint => ({ assetId: f.id, x: f.x, y: f.y, ts: f.lastSeenMs || now }));
      return { positionAccumulator: [...pruned, ...fresh] };
    });
  },
}));
